import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "./api";
import { toast } from "sonner";

/**
 * On `?report=<id>` query-param, fetch the saved report from the backend,
 * call `setForm(report.birth_details)` and `setResult(report.report)` so the
 * page renders the cached insights immediately — no compute, no payment.
 *
 * Must be called from any tool page that wants to support deep-linked saved
 * reports.
 */
export function useSavedReportLoader({ expectedTool, setForm, setResult }) {
  const [params, setParams] = useSearchParams();
  const ranRef = useRef(false);
  useEffect(() => {
    const reportId = params.get("report");
    if (!reportId || ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`);
        if (expectedTool && data.tool && data.tool !== expectedTool) {
          toast.error("That report belongs to a different tool.");
          return;
        }
        if (data.birth_details) setForm?.(data.birth_details);
        if (data.report) setResult?.(data.report);
        // Strip the param so further form edits don't keep re-loading
        const next = new URLSearchParams(params);
        next.delete("report");
        setParams(next, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load that saved report.");
      }
    })();
  }, [params, expectedTool, setForm, setResult, setParams]);
}
